import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ReportApi } from '@app/+authenticated/+reports/shared/api/ReportApi';
import { reportOptions } from '@app/reducers/orm/reports/report.model';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '@sb/ui/toast';
import { catchError, from, Subscription, switchMap, type Observable } from 'rxjs';

@Component({
  standalone: false,
  selector: 'universal-link',
  templateUrl: './universal-link.component.html',
})
export class UniversalLinkComponent implements OnInit, OnDestroy {
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  #reportApi = inject(ReportApi);
  #toast = inject(ToastService);
  #translate = inject(TranslateService);

  private dataSubs = new Subscription();

  ngOnInit(): void {
    this.dataSubs.add(
      this.activatedRoute.paramMap
        .pipe(
          // switchMap drops a pending report lookup when the link changes, and the unsubscribe on
          // destroy drops it when the user leaves, so a late response cannot redirect them.
          switchMap((params) =>
            params.get('type') === 'report'
              ? this.#redirectToReport(params.get('id'))
              : from(this.redirectTo(params.get('type'), params.get('id'))),
          ),
        )
        .subscribe(),
    );
  }

  ngOnDestroy(): void {
    this.dataSubs.unsubscribe();
  }

  private async redirectTo(type: string, id: string) {
    switch (type) {
      case 'leave':
        await this.router.navigate(['my-account/absence']);
        break;
      case 'employees':
        await this.router.navigate([type, id, 'overview']);
        break;
      case 'news':
        await this.router.navigate(['news', { newsId: id }]);
        break;
      case 'employee-absence':
        await this.router.navigateByUrl(`employees/${id}/absence`);
        break;
      case 'my-schedule':
        await this.router.navigateByUrl(`my-account/schedule`);
        break;
      case 'my-absence':
        await this.router.navigateByUrl(`my-account/absence`);
        break;
      case 'insights':
        await this.router.navigateByUrl(`insights`);
        break;
      case 'exchange-request':
      case 'schedule-open-shift':
      case 'absence-request':
        await this.router.navigateByUrl(`dashboard/my-overview(modal:${type}/${id})`);
        break;
      case 'my-account':
        await this.router.navigateByUrl(`my-account/login`);
        break;
      case 'my-rewards':
        await this.router.navigateByUrl(`dashboard/my-overview?cello-open=true`);
        break;
      case 'labs':
        await this.router.navigateByUrl(`dashboard/my-overview?labs-open=true`);
        break;

      default:
        await this.router.navigate(['dashboard/my-overview']);
        break;
    }
  }

  // The link carries only the report id, but its page lives under the report type.
  #redirectToReport(reportId: string | null): Observable<boolean> {
    return this.#reportApi.getReports().pipe(
      switchMap((reports) => {
        const report = reports.find((candidate) => candidate.id === reportId);
        const reportOption = report && reportOptions[report.type];

        if (!reportOption) {
          this.#toast.openDanger(this.#translate.instant('This report is no longer available'));
          return this.router.navigateByUrl('reports', { replaceUrl: true });
        }

        return this.router.navigate(['reports', reportOption.url, report.id], { replaceUrl: true });
      }),
      // The failed request already surfaced its own error, and says nothing about this report.
      catchError(() => this.router.navigateByUrl('reports', { replaceUrl: true })),
    );
  }
}
